import axios from 'axios';
import { API_BASE_URL } from '../components/config/apiConfig';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('adminToken');
};

// Create axios instance with auth headers
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // Check if the token has expired
      const tokenExpires = localStorage.getItem('adminTokenExpires');
      if (tokenExpires && new Date(tokenExpires) <= new Date()) {
        // Clear expired token data
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminId');
        localStorage.removeItem('adminName');
        localStorage.removeItem('adminEmail');
        localStorage.removeItem('adminIsSuperAdmin');
        localStorage.removeItem('adminIsViewOnly');
        localStorage.removeItem('adminTokenExpires');
      }
      
      // Check if we're not already on the login page to avoid redirect loops
      if (!window.location.pathname.includes('/admin/login')) {
        console.log('Authentication error: Redirecting to login...');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

const adminService = {
  // Admin auth methods
  login(credentials) {
    return apiClient.post('/admin/login', credentials)
      .then(response => {
        // Save token to localStorage when logging in
        if (response.data && response.data.token) {
          localStorage.setItem('adminToken', response.data.token);
        }
        return response.data;
      });
  },
  
  logout() {
    // Clear admin token from localStorage
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminId');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminIsSuperAdmin');
    localStorage.removeItem('adminIsViewOnly');
    localStorage.removeItem('adminTokenExpires');
  },
  
  getProfile() {
    return apiClient.get('/admin/profile')
      .then(response => response.data);
  },
  
  changePassword(passwordData) {
    return apiClient.post('/admin/change-password', passwordData)
      .then(response => response.data);
  },
  
  // Admin management methods
  getAdmins() {
    return apiClient.get('/admin/admins')
      .then(response => response.data);
  },
  
  getAdmin(id) {
    return apiClient.get(`/admin/admins/${id}`)
      .then(response => response.data);
  },
  
  createAdmin(adminData) {
    return apiClient.post('/admin/admins', adminData)
      .then(response => response.data);
  },
  
  updateAdmin(id, adminData) {
    return apiClient.put(`/admin/admins/${id}`, adminData)
      .then(response => response.data);
  },
  
  deleteAdmin(id) {
    return apiClient.delete(`/admin/admins/${id}`)
      .then(response => response.data);
  },
  
  // Settings methods
  getSettings() {
    return apiClient.get('/admin/settings')
      .then(response => {
        const data = response.data;
        
        // Convert string boolean values to actual booleans for notifications
        if (data.notifications) {
          Object.keys(data.notifications).forEach(key => {
            if (data.notifications[key] === 'true') {
              data.notifications[key] = true;
            } else if (data.notifications[key] === 'false') {
              data.notifications[key] = false;
            }
          });
        }
        
        return data;
      });
  },
  
  updateSettings(settingsData) {
    return apiClient.put('/admin/settings', settingsData)
      .then(response => response.data);
  },

  // Add missing methods for other admin functions
  getDashboardStats() {
    return apiClient.get('/admin/dashboard')
      .then(response => response.data);
  },

  // For programs
  getPrograms() {
    return apiClient.get('/admin/programs')
      .then(response => response.data);
  },

  getProgram(id) {
    return apiClient.get(`/admin/programs/${id}`)
      .then(response => response.data);
  },

  createProgram(programData) {
    return apiClient.post('/admin/programs', programData)
      .then(response => response.data);
  },

  updateProgram(id, programData) {
    return apiClient.put(`/admin/programs/${id}`, programData)
      .then(response => response.data);
  },

  deleteProgram(id) {
    return apiClient.delete(`/admin/programs/${id}`)
      .then(response => response.data);
  },

  // Program Participants methods
  getProgramParticipants(programId, params = {}) {
    return apiClient.get(`/admin/programs/${programId}/participants`, { params })
      .then(response => response.data);
  },

  downloadProgramParticipants(programId, params = {}) {
    return apiClient.get(`/admin/programs/${programId}/participants/download`, { 
      params,
      responseType: 'blob'
    })
    .then(response => {
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      
      // Try to get filename from content-disposition header, or use default
      let filename = 'participants.csv';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.*?)(;|$)/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1].replace(/["']/g, '');
        }
      }
      
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return response;
    });
  },

  // Program Registrations deletion (pending only)
  deletePendingRegistrations(programId, params = {}) {
    return apiClient.delete(`/admin/programs/${programId}/registrations/pending`, { params })
      .then(response => response.data);
  },

  deleteSingleRegistration(programId, registrationId, params = {}) {
    return apiClient.delete(`/admin/programs/${programId}/registrations/${registrationId}`, { params })
      .then(response => response.data);
  },

  // For resources
  getResources(params = {}) {
    return apiClient.get('/admin/resources', { params })
      .then(response => response.data);
  },

  getResource(id) {
    return apiClient.get(`/admin/resources/${id}`)
      .then(response => response.data);
  },

  createResource(resourceData) {
    return apiClient.post('/admin/resources', resourceData)
      .then(response => response.data);
  },

  updateResource(id, resourceData) {
    return apiClient.put(`/admin/resources/${id}`, resourceData)
      .then(response => response.data);
  },

  deleteResource(id) {
    return apiClient.delete(`/admin/resources/${id}`)
      .then(response => response.data);
  },

  // Membership Plans methods
  getMembershipPlans(params = {}) {
    return apiClient.get('/admin/membership-plans', { params })
      .then(response => response.data);
  },

  getMembershipPlan(id) {
    return apiClient.get(`/admin/membership-plans/${id}`)
      .then(response => response.data);
  },

  getMembershipPlanStats(id) {
    return apiClient.get(`/admin/membership-plans/${id}/stats`)
      .then(response => response.data);
  },

  createMembershipPlan(planData) {
    return apiClient.post('/admin/membership-plans', planData)
      .then(response => response.data);
  },

  updateMembershipPlan(id, planData) {
    return apiClient.put(`/admin/membership-plans/${id}`, planData)
      .then(response => response.data);
  },

  deleteMembershipPlan(id) {
    return apiClient.delete(`/admin/membership-plans/${id}`)
      .then(response => response.data);
  },

  toggleMembershipPlanStatus(id) {
    return apiClient.put(`/admin/membership-plans/${id}/toggle-status`)
      .then(response => response.data);
  },

  // Discount methods
  getDiscounts(params = {}) {
    return apiClient.get('/admin/discounts', { params })
      .then(response => response.data);
  },

  getDiscount(id) {
    return apiClient.get(`/admin/discounts/${id}`)
      .then(response => response.data);
  },

  createDiscount(discountData) {
    return apiClient.post('/admin/discounts', discountData)
      .then(response => response.data);
  },

  updateDiscount(id, discountData) {
    return apiClient.put(`/admin/discounts/${id}`, discountData)
      .then(response => response.data);
  },

  deleteDiscount(id) {
    return apiClient.delete(`/admin/discounts/${id}`)
      .then(response => response.data);
  },

  toggleDiscountStatus(id, status) {
    return apiClient.put(`/admin/discounts/${id}`, { status })
      .then(response => response.data);
  },

  // Professional Listings Management
  getProfessionalListings(params = {}) {
    return apiClient.get('/admin/professional-listings', { params })
      .then(response => response.data);
  },
  
  getProfessionalListing(id) {
    return apiClient.get(`/admin/professional-listings/${id}`)
      .then(response => response.data);
  },
  
  createProfessionalListing(professionalData) {
    return apiClient.post('/admin/professional-listings', professionalData)
      .then(response => response.data);
  },
  
  updateProfessionalListing(id, professionalData) {
    return apiClient.put(`/admin/professional-listings/${id}`, professionalData)
      .then(response => response.data);
  },
  
  deleteProfessionalListing(id) {
    return apiClient.delete(`/admin/professional-listings/${id}`)
      .then(response => response.data);
  },
  
  toggleProfessionalListingStatus(id) {
    return apiClient.put(`/admin/professional-listings/${id}/toggle-status`)
      .then(response => response.data);
  },
  
  // Members methods
  getMembers(params = {}) {
    return apiClient.get('/admin/members', { params })
      .then(response => response.data);
  },

  getMember(id) {
    return apiClient.get(`/admin/members/${id}`)
      .then(response => response.data);
  },

  createMember(memberData) {
    return apiClient.post('/admin/members', memberData)
      .then(response => response.data);
  },

  updateMember(id, memberData) {
    return apiClient.put(`/admin/members/${id}`, memberData)
      .then(response => response.data);
  },

  deleteMember(id) {
    return apiClient.delete(`/admin/members/${id}`)
      .then(response => response.data);
  },

  exportMembers(params = {}) {
    return apiClient.get('/admin/members/export', { 
      params,
      responseType: 'blob'
    })
    .then(response => {
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      
      let filename = 'members.csv';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.*?)(;|$)/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1].replace(/["']/g, '');
        }
      }
      
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return response;
    });
  },

  // Professional Listing Pricing
  getProfessionalListingPrice() {
    return apiClient.get('/admin/professional-listing-price')
      .then(response => response.data);
  },
  
  updateProfessionalListingPrice(pricingData) {
    return apiClient.put('/admin/professional-listing-price', pricingData)
      .then(response => response.data);
  },
  
  // New endpoints for managing member vs non-member pricing
  updateProfessionalListingPricingOptions(pricingOptions) {
    return apiClient.put('/admin/professional-listing-pricing-options', pricingOptions)
      .then(response => response.data);
  },
  
  getProfessionalListingPricingOptions() {
    return apiClient.get('/admin/professional-listing-pricing-options')
      .then(response => response.data);
  },
  
  // Create default membership plans - fixed to use apiClient instead of api
  createDefaultMembershipPlans: async () => {
    const response = await apiClient.post('/admin/membership-plans/create-defaults');
    return response.data;
  }
};

export default adminService;
